/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

// Component imports
import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Form, Grid, Input, Menu, Popconfirm, Row } from 'antd';
import MediaGridTable from '../components/Tables/MediaGridTable';
import DocumentTitle from '../components/DocumentTitle';
import MediaTable from '../components/Tables/MediaTable';
import MediaUpload from '../components/UI/MediaUpload';
import { MediaGridIcon } from '../components/Icons/MediaGridIcon';
import { MediaTableIcon } from '../components/Icons/MediaTableIcon';
import { PlusIcon } from '../components/Icons/PlusIcon';
import { ArrowDownIcon } from '../components/Icons/ArrowDownIcon';

// Misc imports
import useDebouncedCallback from '../utils/hooks/useDebounce';
import { MEDIA_LIBRARY_VIEWS } from '../constants/main';
import { changeMediaView } from '../store/media/mediaActions';
import { useCreateMediaFileMutation, useDeleteMediaFileMutation, useDeleteMediaFilesMutation, useFetchMediaFilesQuery } from '../store/media/mediaApi';

// Constant declarations
const { useBreakpoint } = Grid;
const PAGE_SIZE = 32;

const MediaScreen = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [isUploadVisible, setIsUploadVisible] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [isBulkEnabled, setIsBulkEnabled] = useState(false);
  const { mediaView } = useSelector((state) => state.mediaFiles);
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const [searchParams] = useSearchParams();

  const { isFetching: isFetchingMediaFiles } = useFetchMediaFilesQuery({ orgId, urlParams: {
    view: searchParams.get('view'),
    page: searchParams.get('page') || page,
    pageSize: searchParams.get('pageSize') || PAGE_SIZE,
    sortBy: searchParams.get('sortBy') || 'createdAt',
    order: searchParams.get('order') || 'DESC',
    ...(searchParams.get('search') && { search: searchParams.get('search') }),
    ...(searchParams.get('fileType') && { fileType: searchParams.get('fileType') }),
    ...(searchParams.get('createdBy') && { createdBy: searchParams.get('createdBy') })
  } });

  const [deleteMediaFile, { isLoading: isDeletingMediaFile }] = useDeleteMediaFileMutation({ fixedCacheKey: 'mediaFiles-delete' });
  const [deleteMediaFiles, { isLoading: isDeletingMediaFiles }] = useDeleteMediaFilesMutation();
  const [, { isLoading: isCreatingMediaFile }] = useCreateMediaFileMutation({ fixedCacheKey: 'mediaFiles-create' });

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    if (urlSearchParams.has('view')) {
      dispatch(changeMediaView(urlSearchParams.get('view')));
    }

    updateUrlSearchParams();
  }, []);

  useEffect(() => {
    selectedFiles?.length !== 0 ? setIsBulkEnabled(true) : setIsBulkEnabled(false);
  }, [selectedFiles]);

  const openUploadPanel = () => {
    setIsUploadVisible(true);
  };

  const handleDelete = async (data) => {
    let isDeleteSuccessful = await deleteMediaFile({ orgId, id: data.id }).unwrap();

    if (isDeleteSuccessful) {
      if (selectedFiles.includes(data.id)) {
        setSelectedFiles(selectedFiles.filter(item => item !== data.id));
      }
    }
  };

  const handleBatchDelete = async (data) => {
    let isDeleteSuccessful = await deleteMediaFiles({ orgId, ids: { ids: data } }).unwrap();

    if (isDeleteSuccessful) {
      setSelectedFiles([]);
      setIsBulkEnabled(false);
    }
  };

  const updateUrlSearchParams = async (current = 1, pageSize= PAGE_SIZE, sort = { sortBy: 'createdAt', order: 'DESC' }, fileTypeFilter, createdByFilter) => {
    setPage(current);
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set('page', current);
    urlSearchParams.set('pageSize', pageSize);
    const search = form.getFieldValue('search');

    if (search) {
      urlSearchParams.set('search', encodeURIComponent(search));
    } else {
      urlSearchParams.delete('search');
    }

    if (fileTypeFilter) {
      urlSearchParams.set('fileType', encodeURIComponent(fileTypeFilter));
    } else {
      urlSearchParams.delete('fileType');
    }

    if (createdByFilter) {
      urlSearchParams.set('createdBy', encodeURIComponent(createdByFilter));
    } else {
      urlSearchParams.delete('createdBy');
    }

    if (sort.sortBy && sort.order) {
      urlSearchParams.set('sortBy', sort.sortBy);
      urlSearchParams.set('order', sort.order);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    urlSearchParams.set('view', encodeURIComponent(mediaView));

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const makeApiSearchParamsFromUrl = () => {
    const urlSearchParams = new URLSearchParams(location.search);
    return Array.from(urlSearchParams.entries()).map(([key, value]) => `${key}=${value}`);
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);

  const menu = (
    <Menu
      items={[
        {
          key: 'delete',
          label: (
            <Popconfirm
              title={t('Are you sure you want to delete these files?')}
              okText={t('Yes')}
              cancelText={t('No')}
              placement="topRight"
              onConfirm={() => handleBatchDelete(selectedFiles)}
            >
              {t('Delete Selected')}
            </Popconfirm>
          )
        }
      ]}
    />
  );

  return (
    <Fragment>
      <DocumentTitle title={t('Media Library')} />

      <Row gutter={24} wrap={screens.xs && !screens.sm}>
        <Col flex="auto" className="mb-2">
          <Row wrap={true} gutter={8}>
            <Col>
              <Form
                layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
                name="mediaFilesSearch"
                initialValues={{
                  searchTerm: ''
                }}
                form={form}
              >
                <Form.Item
                  name="search"
                  className={isMobileOrTabletSize ? 'mb-2' : ''}
                >
                  <Input
                    size="large"
                    placeholder={t('Search in contents')}
                    allowClear
                    prefix={<SearchOutlined className="text-muted" />}
                    value={form.getFieldValue('search')}
                    onChange={(e) => {
                      debouncedOnChange();
                      form.setFieldsValue({ search: e.target.value });
                    }}
                  />
                </Form.Item>
              </Form>
            </Col>

            <Col>
              <Button
                type="text"
                size="large"
                className="p-0"
                onClick={() => {
                  dispatch(changeMediaView(MEDIA_LIBRARY_VIEWS.GRID));
                }}
              >
                <MediaGridIcon />
              </Button>

              <Button
                type="text"
                size="large"
                className="p-0"
                onClick={() => {
                  dispatch(changeMediaView(MEDIA_LIBRARY_VIEWS.TABLE));
                }}
              >
                <MediaTableIcon />
              </Button>
            </Col>
          </Row>

        </Col>

        <Col
          flex={screens.xs ? 'auto' : 'none'}
          className="mb-2"
        >
          <Row wrap={true} gutter={8}>
            <Col flex="auto" className="mb-2">
              <Dropdown
                overlay={menu}
                disabled={!isBulkEnabled}
              >
                <Button
                  size="large"
                  block
                  loading={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile || isDeletingMediaFiles}
                >
                  {t('Bulk Actions')}
                  <ArrowDownIcon />
                </Button>
              </Dropdown>
            </Col>

            <Col flex="auto" className="mb-2">
              <Button
                type="primary"
                size="large"
                icon={<PlusIcon />}
                onClick={openUploadPanel}
                loading={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
              >
                {t('Upload New')}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <MediaUpload hasCloseButton={true} isUploadVisible={isUploadVisible} setIsUploadVisible={setIsUploadVisible} makeApiSearchParamsFromUrl={makeApiSearchParamsFromUrl} />

      {
        mediaView === MEDIA_LIBRARY_VIEWS.GRID
          ? <MediaGridTable page={page} gridSize={3} imageHeight={200} hasPreview={true} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} handleDelete={handleDelete} urlSearchParams={updateUrlSearchParams} />
          : <MediaTable page={page} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} handleDelete={handleDelete} urlSearchParams={updateUrlSearchParams} />
      }

    </Fragment>
  );
};

export default MediaScreen;
